import React, { useState } from 'react';
import { collection, deleteDoc, doc, getDocs, orderBy, query, where } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useStore } from '../store';
import type { Feedback as FeedbackType, Game, Profile } from '../types';
import { Search, Calendar, MessageSquare, Link as LinkIcon, Send, Inbox, ExternalLink, Trash2, Target, Pencil, ChevronDown, ChevronUp, Dumbbell } from 'lucide-react';
import { Link } from 'react-router-dom';
import DrillDrawingCanvas from './DrillDrawingCanvas';
import { useTranslation } from '../i18n/useTranslation';

interface FeedbackListProps {
  feedback: FeedbackType[];
  onFeedbackDeleted?: () => void;
  viewMode: 'received' | 'sent';
  gameOwners: Record<string, Profile>;
  games: Record<string, Game>;
}

export default function FeedbackList({ feedback, onFeedbackDeleted, viewMode, gameOwners, games }: FeedbackListProps) {
  const { t } = useTranslation();
  const user = useStore((state) => state.user);
  const [deletingFeedback, setDeletingFeedback] = useState<string | null>(null);
  const [expandedFeedback, setExpandedFeedback] = useState<Set<string>>(new Set());
  const [expandedDrills, setExpandedDrills] = useState<Record<string, boolean>>({});

  const handleDelete = async (feedbackId: string) => {
    try {
      await deleteDoc(doc(db, 'feedback', feedbackId));
      onFeedbackDeleted?.();
    } catch (error) {
      console.error('Error deleting feedback:', error);
    }
  };

  const toggleFeedback = (feedbackId: string) => {
    setExpandedFeedback(prev => {
      const newSet = new Set(prev);
      if (newSet.has(feedbackId)) {
        newSet.delete(feedbackId);
      } else {
        newSet.add(feedbackId);
      }
      return newSet;
    });
  };

  const toggleDrillDrawing = (feedbackId: string, drillIndex: number) => {
    const key = `${feedbackId}-${drillIndex}`;
    setExpandedDrills(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (feedback.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        {t('feedbackList.noFeedbackYet')}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {feedback.map((item) => (
        <div key={item.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
          <button
            onClick={() => toggleFeedback(item.id)}
            className="w-full px-6 py-4 flex justify-between items-center hover:bg-gray-50 transition-colors"
          >
            <div className="flex-1 text-left">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-gray-400" />
                <span className="font-medium text-gray-900">
                  {viewMode === 'received' 
                    ? item.authorName 
                    : gameOwners[item.gameId] 
                      ? `${gameOwners[item.gameId].firstName} ${gameOwners[item.gameId].lastName}`
                      : t('feedbackList.unknownRecipient')}
                </span>
              </div>
              {games[item.gameId] && (
                <div className="flex items-center gap-2 ml-6 mt-1">
                  <Target className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-600">
                    Game vs {games[item.gameId].opponent} ({games[item.gameId].date.toLocaleDateString()})
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-500">
                {item.createdAt.toLocaleDateString()}
              </span>
              {expandedFeedback.has(item.id) ? (
                <ChevronUp className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-400" />
              )}
            </div>
          </button>

          {expandedFeedback.has(item.id) && (
            <div className="px-6 py-4 border-t border-gray-100">
              <p className="text-gray-700 whitespace-pre-wrap mb-4">{item.content}</p>
              
              {item.drills && item.drills.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Dumbbell className="h-4 w-4 text-[#1b3968]" />
                    <h4 className="font-medium text-[#1b3968]">{t('feedbackList.suggestedDrills')}</h4>
                  </div>
                  <div className="space-y-3">
                    {item.drills.map((drill, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                        <h5 className="font-medium text-gray-900">{drill.title}</h5>
                        <p className="text-gray-600 text-sm mt-1">{drill.description}</p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {drill.videoUrl && (
                            <a
                              href={drill.videoUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#1b3968] hover:text-[#1b3968]/80 text-sm flex items-center gap-1"
                            >
                              <LinkIcon className="h-4 w-4" />
                              {t('feedbackList.watchVideo')}
                            </a>
                          )}
                          {drill.exerciseDrawing && drill.exerciseDrawing.length > 0 && (
                            <div className="w-full">
                              <button
                                onClick={() => toggleDrillDrawing(item.id, index)}
                                className="px-3 py-1.5 border border-[#1b3968] text-[#1b3968] hover:bg-[#1b3968] hover:text-white rounded-md text-sm flex items-center gap-2 transition-colors"
                              >
                                <Pencil className="h-4 w-4" />
                                {t('feedbackList.exerciseDrawing')}
                                {expandedDrills[`${item.id}-${index}`] ? (
                                  <ChevronUp className="h-4 w-4" />
                                ) : (
                                  <ChevronDown className="h-4 w-4" />
                                )}
                              </button>
                              {expandedDrills[`${item.id}-${index}`] && (
                                <div className="mt-3">
                                  <DrillDrawingCanvas
                                    points={drill.exerciseDrawing}
                                    readOnly={true}
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
                <Link
                  to={`/feedback/${item.id}`}
                  className="text-[#1b3968] hover:text-[#1b3968]/80 text-sm flex items-center gap-1"
                >
                  <ExternalLink className="h-4 w-4" />
                  {t('feedbackList.viewDetails')}
                </Link>
                {(user?.uid === item.authorId) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeletingFeedback(item.id);
                    }}
                    className="text-gray-400 hover:text-red-500 transition-colors"
                    title={t('feedbackList.deleteFeedback')}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Delete Confirmation Modal */}
      {deletingFeedback && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4">{t('feedbackList.deleteFeedbackTitle')}</h2>
            <p className="text-gray-600 mb-6">
              {t('feedbackList.deleteFeedbackConfirmation')}
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setDeletingFeedback(null)}
                className="text-gray-600 hover:text-gray-900"
              >
                {t('feedbackList.cancel')}
              </button>
              <button
                onClick={() => {
                  if (deletingFeedback) {
                    handleDelete(deletingFeedback);
                    setDeletingFeedback(null);
                  }
                }}
                className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600"
              >
                {t('feedbackList.deleteFeedback')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}